import React, { useEffect, useState } from "react";
import './styles.css'
import { useNavigate } from 'react-router-dom';
import { APP_CONSTANTS, ROUTES } from "../../../utils/AppConstants";
import { clearLocalStorage, displayErrorToast, isEmpty, isValidEmail, screenCaptureAnalytics } from "../../../utils/Utils";
import { ToastContainer } from 'react-toastify';
import LandingNavBar from "../landing-nav-bar/landingNavBar";
import { PLAN_PRICING } from "../../../utils/PricingPlan";
import { useDispatch } from 'react-redux';
import { config } from "../../../config";
import PageFooter from "../page-footer/pageFooter";

const LandingPage = props => {
    let navigate = useNavigate();
    const [activeFaqIndex, setActiveFaqIndex] = useState(null);
    const [email, setEmail] = useState('')
    const dispatch = useDispatch()

    useEffect(() => {
        if (config.environment === APP_CONSTANTS.ENVIRONMENT.PROD) {
            const data = {
                screen: 'HOME_PAGE',
                screenClass: 'HOME_PAGE'
            }
            screenCaptureAnalytics(data)
        }
        clearLocalStorage()
        sessionStorage.clear()
        dispatch({
            type: APP_CONSTANTS.LOGOUT
        })
    }, [])

    const handleFaqToggle = (index) => {
        setActiveFaqIndex(activeFaqIndex === index ? null : index);
    };

    const reasons = [
        {
            title: "Granular Control Meets Simplicity",
            icon: require('../../../assets/ic_highlight_control.png'),
            points: [
                "Feature-level access controls for precise permission management",
                "Role-based + individual employee permission settings",
                "Visual access matrix for easy configuration"
            ]
        },
        {
            title: "Truly Scalable Architecture",
            icon: require('../../../assets/ic_highlight_scale.png'),
            points: [
                "From 5 to 50,000+ employees - grows with you",
                "Unlimited businesses & locations management",
                "Enterprise-grade performance at startup-friendly pricing"
            ]
        },
        {
            title: "All-in-One Workforce Solution",
            icon: require('../../../assets/ic_highlight_all.png'),
            points: [
                "HR + Payroll + Assets in one unified platform",
                "No need for multiple integrations",
                "Unified employee experience across functions"
            ]
        },
        {
            title: "Risk-Free Implementation",
            icon: require('../../../assets/ic_highlight_security.png'),
            points: [
                "1-month full-feature free trial",
                "No credit card required",
                "Guided onboarding & 24/7 support"
            ]
        }
    ];

    const faqList = [
        {
            id: 1,
            question: 'What is Togethra and how does it work?',
            lineOne: 'Togethra is a workforce management tool that lets you start, grow and manage a business',
            lineTwo: 'With Togethra, you can:',
            points: [
                'Manage unlimited businesses and locations under a single account.',
                'Track employee attendance, create custom shifts, and monitor schedules in real-time.',
                'Assign, track, and prioritize tasks with deadline alerts and progress tracking.',
                'Built-in communication tool for seamless team collaboration.',
                'Centralized storage for policies, handbooks, and company documents with version control.'
            ]
        },
        {
            id: 2,
            question: 'How much does Togethra cost?',
            lineOne: 'Try Togethra free for 1 month, no credit card required.',
            lineTwo: 'After your trial expires, choose a pricing plan that suits the size and stage of your business.',
            points: []
        },
        {
            id: 3,
            question: 'Is this product suitable for small businesses?',
            lineOne: 'Yes, it is designed to scale for businesses of all sizes.',
            lineTwo: '',
            points: []
        },
        {
            id: 4,
            question: 'Can employees access the platform?',
            lineOne: 'Yes, employees can access their schedules, tasks, and messages through dedicated employee accounts.',
            lineTwo: '',
            points: []
        },
        {
            id: 5,
            question: 'Can I customize permissions for individual employees?',
            lineOne: 'Admins can set permissions during employee onboarding or role creation. Each feature can be toggled on/off per role, ensuring employees only access approved functionalities.',
            lineTwo: '',
            points: []
        },
        {
            id: 6,
            question: 'How secure is the data?',
            lineOne: 'We ensure top-tier security measures and use enterprise-grade encryption to protect your data.',
            lineTwo: '',
            points: []
        }
    ]

    const handleCreateAccountClick = () => {
        if (isEmpty(email)) {
            displayErrorToast('Email address cannot be blank')
            return
        }
        if (!isEmpty(email) && !isValidEmail(email)) {
            displayErrorToast('Invalid email address')
            return
        }
        navigate(ROUTES.businessCreateAccount, {
            state: {
                email: email,
                startFreeTrail: true
            }
        })
    }

    return (
        <>
            <LandingNavBar />
            <div className="lp-hero-section">
                <h1 className="lp-title">Streamline Your Workforce, Effortlessly & Free</h1>
                <p className="lp-sub-title">Built for Every Stage—From Startup to Enterprise. Sign up for a free trial and enjoy your first month without any cost.</p>
                <div className="lp-email-container">
                    <input
                        type="email"
                        placeholder="Enter your email address"
                        className="lp-email-input"
                        onChange={e => setEmail(e.target.value)}
                        value={email}
                    />
                    <div className="lp-email-button" onClick={() => { handleCreateAccountClick() }}>Start free trial</div>
                </div>
                <div className="lp-email-button-mobile" onClick={() => { handleCreateAccountClick() }}>Start free trial</div>
                <div className="lp-img-container">
                    <img src={require('../../../assets/lp_img_center.png')} className="img" alt="img" />
                </div>
            </div>
            <section className="lp-quote-section">
                <blockquote className="lp-quote-label">"The best way to manage your employees efficiently and boost productivity effortlessly."</blockquote>
                <div className="lp-quote-author-label">―Togethra</div>
            </section>
            <section className="lp-choose-product-section">
                <h2 className="lp-title">Why Choose Our Product?</h2>
                <p className="mt-4 lp-sub-title lp-sub-title-margin">
                    Our all-in-one employee management solution simplifies employee management, tracking and performance.
                </p>
                <div className="lp-choose-us-row-container">
                    {reasons.map((reason, index) => (
                        <div
                            key={index}
                            className="lp-choose-us-row-item-container"
                        >
                            <div className="lp-curc-label-container">
                                <div className="lp-cur-img-container">
                                    <img src={reason.icon} className="img" alt="img" />
                                </div>
                                <h3 className="lp-curc-label">
                                    {reason.title}
                                </h3>
                            </div>
                            <div className="lp-curc-point-container">
                                {reason.points.map(item => (<div className="lp-curc-point-row-container">
                                    <div className="lp-curc-point-row-img-cont">
                                        <img src={require('../../../assets/ic_tick_icon.png')} className="img" alt="img" />
                                    </div>
                                    <div className="lp-curc-point-row-label">{item}</div>
                                </div>))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <section className="lp-faq-section">
                <h2 className="lp-faq-label">Frequently Asked Questions</h2>
                <div className="lp-faq-row-item-container">
                    {faqList.map((item, index) => (
                        <div key={index} className="lp-faq-row-content-container margin-bottom-10">
                            <div className="faq-ques-row-container" onClick={() => handleFaqToggle(index)}>
                                <h3 className="lp-faq-question-label">{item.question}</h3>
                                <div className="lp-faq-icon-container">
                                    {activeFaqIndex !== index && <img src={require('../../../assets/ic_plus_icon.png')} className="img" alt="img" />}
                                    {activeFaqIndex === index && <img src={require('../../../assets/ic_minus_icon.png')} className="img" alt="img" />}
                                </div>
                            </div>
                            {activeFaqIndex === index && <div>
                                {!isEmpty(item.lineOne) && <p className="lp-faq-ans-label faq-margin-bottom-10">{item.lineOne}</p>}
                                {!isEmpty(item.lineTwo) && <p className="lp-faq-ans-label faq-margin-bottom-10">{item.lineTwo}</p>}
                                {!isEmpty(item.points) && item.points.map(e => (<p className="lp-faq-ans-label faq-margin-bottom-5 faq-padding-left-15">✦ {e}</p>))}
                            </div>}
                        </div>
                    ))}
                </div>
            </section>
            <PageFooter isHomePage={true} />
            <ToastContainer />
        </>
    )
}

export default LandingPage